import { Loader2, Link, ImageIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateItemMutation } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import type { Item, ItemTagData } from "@/modules/mine/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
type EditItemResult =
  | {
      action: "updated";
      itemId: string;
    }
  | {
      action: "deleted";
      itemId: string;
    };

interface EditItemProps extends DialogComponentProps<EditItemResult> {
  item: Item;
  onDelete?: (item: Item, resolve: (value: EditItemResult) => void) => void;
}

function buildUpdateWebsitePayload(params: {
  coverUrl: string;
  item: Item;
  title: string;
  url: string;
  note: string;
  selectedFolderId: string | null;
  tags: ItemTagData[];
}) {
  const { coverUrl, item, title, url, note, selectedFolderId, tags } = params;

  return {
    id: item.id,
    title: title.trim(),
    url: url.trim(),
    note: note.trim(),
    folder_id: selectedFolderId || undefined,
    tags: {
      tag_ids: tags.map((tag) => tag.id),
    },
    website: {
      cover_url: coverUrl.trim(),
    },
  };
}

export function EditItem({ close, resolve, item }: EditItemProps) {
  // 表单状态
  const [title, setTitle] = useState(item?.title || "");
  const [url, setUrl] = useState(item?.url || "");
  const [note, setNote] = useState(item?.note || "");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    item?.folder_id || null,
  );
  const [coverUrl, setCoverUrl] = useState(item?.website?.cover_url || "");
  const [tags, setTags] = useState<ItemTagData[]>(() => item?.tags ?? []);
  const [fieldErrors, setFieldErrors] = useState<{
    title?: string;
    url?: string;
  }>({});
  const updateItemMutation = useUpdateItemMutation();

  // 提交表单
  const handleSubmit = () => {
    const trimmedTitle = title.trim();
    const trimmedUrl = url.trim();
    const nextErrors: typeof fieldErrors = {};

    if (!trimmedTitle) {
      nextErrors.title = "请输入标题";
    }

    if (!trimmedUrl) {
      nextErrors.url = "请输入网址 URL";
    }

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    updateItemMutation.mutate(
      buildUpdateWebsitePayload({
        coverUrl,
        item,
        title: trimmedTitle,
        url: trimmedUrl,
        note,
        selectedFolderId,
        tags,
      }),
      {
        onSuccess: () => {
          toast.success("保存成功");
          resolve({
            action: "updated",
            itemId: item.id,
          });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      {/* 两栏布局 */}
      <div className="grid grid-cols-[3fr_2fr] gap-6">
        {/* 左栏：主要内容 */}
        <div className="space-y-4">
          {/* 标题 */}
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs text-muted-foreground">
              标题
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (fieldErrors.title) {
                  setFieldErrors((prev) => ({ ...prev, title: undefined }));
                }
              }}
              placeholder="输入标题"
              aria-invalid={Boolean(fieldErrors.title)}
              className={cn(fieldErrors.title && "border-destructive")}
            />
            {fieldErrors.title ? (
              <p className="text-xs text-destructive">{fieldErrors.title}</p>
            ) : null}
          </div>

          {/* URL */}
          <div className="space-y-1.5">
            <Label htmlFor="url" className="text-xs text-muted-foreground">
              网址 URL
            </Label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (fieldErrors.url) {
                    setFieldErrors((prev) => ({ ...prev, url: undefined }));
                  }
                }}
                placeholder="https://example.com"
                aria-invalid={Boolean(fieldErrors.url)}
                className={cn("pl-9", fieldErrors.url && "border-destructive")}
              />
            </div>
            {fieldErrors.url ? (
              <p className="text-xs text-destructive">{fieldErrors.url}</p>
            ) : null}
          </div>

          {/* 详细描述 */}
          <div className="space-y-1.5">
            <Label htmlFor="note" className="text-xs text-muted-foreground">
              详细描述
            </Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="添加描述..."
              className="resize-none min-h-[80px]"
            />
          </div>

          {/* 所属文件夹 */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              所属文件夹
            </Label>
            <FolderSelector
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              placeholder="未分类"
              allowEmpty={true}
              emptyLabel="未分类"
            />
          </div>

          {/* 标签关键词 */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              标签
            </Label>
            <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
              <TagSelector
                value={tags}
                onChange={setTags}
                triggerClassName="bg-muted px-2 hover:bg-muted/80 [&_svg]:size-3"
              />
            </div>
          </div>

        </div>

        {/* 右栏：元数据 */}
        <div className="space-y-4">
          {/* 封面预览 */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              封面预览
            </Label>
            <div className="h-48 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt="封面预览"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <ImageIcon className="size-8 text-muted-foreground/30" />
              )}
            </div>
          </div>

          {/* 封面图片地址 */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">封面图片地址</Label>
            <Input
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground">
              更改链接后将实时刷新预览效果。
            </p>
          </div>
        </div>
      </div>

      {/* 底部操作栏 */}
      <DialogFooter className="pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={updateItemMutation.isPending}
        >
          取消
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={updateItemMutation.isPending}>
          {updateItemMutation.isPending && <Loader2 className="mr-1.5 animate-spin" />}
          确认
        </Button>
      </DialogFooter>
    </div>
  );
}
